import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { App } from "antd";

import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseButton from "@/components_v2/Button/BaseButton";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import { HookFormBaseDropdown } from "@/components_v2/FormFields/ReactHookForm/fields";
import { OrganizationRoles } from "@/shared/utils/constants";
import { useAcceptApplicationByIdMutation } from "@/redux/service";
import { handleErrors } from "@/shared/utils/forms";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import {
  AcceptApplicationFormValues,
  ApplicationAcceptModalProps,
} from "@/features/organizations/profile/types";

import "../styles.css";

const ApplicationAcceptModal = ({
  open,
  setOpen,
  application,
  onAccepted,
}: ApplicationAcceptModalProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const [acceptApplicationById] = useAcceptApplicationByIdMutation();

  const acceptApplicationSchema: yup.ObjectSchema<AcceptApplicationFormValues> =
    yup.object().shape({
      assignedRole: yup
        .string()
        .oneOf(
          OrganizationRoles.asList().map(
            (i: ReturnType<typeof OrganizationRoles.asList>[0]) => i.value,
          ),
          t("organizations.validation.notValid"),
        )
        .typeError(t("organizations.validation.notValid"))
        .required(t("organizations.validation.required")),
    });

  const form = useForm<AcceptApplicationFormValues>({
    resolver: yupResolver(acceptApplicationSchema),
    mode: "onChange",
    defaultValues: {
      assignedRole: "",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset();
    }
  }, [open]);

  const roleOptions = OrganizationRoles.asList().filter(
    (i: ReturnType<typeof OrganizationRoles.asList>[0]) =>
      i.value !== OrganizationRoles.ADMIN.value,
  );

  const onClose = () => setOpen(false);

  const handleSubmit = (values: AcceptApplicationFormValues) => {
    acceptApplicationById({ id: application.id, data: values })
      .unwrap()
      .then(() => {
        notification.success({
          message: t("organizations.labels.applicationAccepted"),
        });
        onClose();
        onAccepted();
      })
      .catch((errors) => handleErrors(errors, form.setError, notification));
  };

  return (
    <BaseModal
      destroyOnClose
      title={t("organizations.labels.acceptApplication")}
      open={open}
      onCancel={onClose}
      footer={
        <div className="modal-footer-buttons">
          <BaseButton
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.TERTIARY}
            onClick={onClose}
          >
            {t("organizations.labels.cancel")}
          </BaseButton>

          <BaseButton
            variant={BUTTON_VARIANTS.PRIMARY}
            size={BUTTON_SIZES.SMALL}
            loading={form.formState.isSubmitting}
            onClick={form.handleSubmit(handleSubmit)}
          >
            {t("organizations.labels.acceptApplication")}
          </BaseButton>
        </div>
      }
    >
      <div className="form-input-container">
        <FormFieldWrapper
          label={t("organizations.labels.assignedRole")}
          required
        >
          <HookFormBaseDropdown
            name="assignedRole"
            options={roleOptions}
            placeholder={t("organizations.placeholders.selectAssignedRole")}
            control={form.control}
          />
        </FormFieldWrapper>
      </div>
    </BaseModal>
  );
};

export default ApplicationAcceptModal;
